// Адресат ответственного (005: FR-033; макеты 5 и 6): пояснение, через кого уйдёт письмо, что
// исправить, чтобы адресат появился, и готовность пункта — адресат есть у каждого ответственного
// (FR-033, сценарий 7.5). Пилюля адресата — общая для всех экранов (app/addressee.tsx, T055).
// 006 (FR-010, FR-011): человек «без почты» — «Пункты руководителю: Морозов О. С.»; упоминание
// подразделения — на общую почту подразделения с копией основному куратору.
import type { AddresseeReason, ControlResponsible, RecordRef } from '@kp/contracts';
import { reasonTitle } from '../../app/addressee.tsx';
import { recordHref } from '../../shell/links.tsx';

function sameRecord(a: RecordRef | null, b: RecordRef | null): boolean {
  return a !== null && b !== null && a.id === b.id;
}

/**
 * Пояснение под ответственным: через кого уйдёт письмо и кому копия, или у кого исправить —
 * «Через основного куратора: Киселёв З. А.», «Нет почты у основного куратора: Киселёв З. А.»,
 * «Пункты руководителю: Морозов О. С.», «На общую почту «ПТО»».
 */
export function addresseeNote(responsible: ControlResponsible): string | null {
  const { addressee, record } = responsible;
  if (addressee.ready) {
    const parts: string[] = [];
    const to = addressee.to;
    if (addressee.via === 'curator' && to) parts.push(`Через основного куратора: ${to.name}`);
    if (addressee.via === 'manager' && to) parts.push(`Пункты руководителю: ${to.name}`);
    if (addressee.via === 'unit_email') {
      parts.push(addressee.unit ? `На общую почту «${addressee.unit.name}»` : 'На общую почту');
    }
    if (addressee.copy) parts.push(`копия — основному куратору: ${addressee.copy.name}`);
    if (parts.length === 0) return null;
    const text = parts.join(', ');
    return text.charAt(0).toLocaleUpperCase('ru-RU') + text.slice(1);
  }
  if (addressee.record && !sameRecord(addressee.record, record)) {
    return `${reasonTitle(addressee.reason)} у основного куратора: ${addressee.record.name}`;
  }
  if (addressee.reason === 'unclear' && !record) return 'Выберите в справочнике, кто это';
  return null;
}

/**
 * Что сделать, чтобы адресат появился: почту — на месте (право directory.write), остальное —
 * в карточке записи справочника: «Подтвердить адрес», «Выбрать куратора», «Открыть карточку».
 */
export type AddresseeFix =
  { kind: 'email'; person: RecordRef } | { kind: 'card'; label: string; href: string } | null;

export function fixFor(
  reason: AddresseeReason,
  target: RecordRef | null,
  canWrite: boolean,
): AddresseeFix {
  if (!target) return null;
  if (reason === 'no_email' && target.kind === 'person' && canWrite) {
    return { kind: 'email', person: target };
  }
  const label =
    reason === 'email_unconfirmed'
      ? 'Подтвердить адрес'
      : reason === 'no_primary_curator'
        ? 'Выбрать куратора'
        : 'Открыть карточку';
  return { kind: 'card', label, href: recordHref(target) };
}

export interface Readiness {
  ready: number;
  total: number;
}

export function readinessOf(responsibles: readonly ControlResponsible[]): Readiness {
  return {
    ready: responsibles.filter((responsible) => responsible.addressee.ready).length,
    total: responsibles.length,
  };
}

/**
 * Строка готовности пункта (макет 6): «Пункт уйдёт, когда адресат будет у всех: 1 из 2»;
 * у всех есть — «Адресат есть у всех: пункт готов к рассылке».
 */
export function readinessText({ ready, total }: Readiness): string {
  if (total === 1) {
    return ready === 1
      ? 'Адресат есть: пункт готов к рассылке'
      : 'Пункт уйдёт, когда у ответственного будет адресат';
  }
  return ready === total
    ? 'Адресат есть у всех: пункт готов к рассылке'
    : `Пункт уйдёт, когда адресат будет у всех: ${ready} из ${total}`;
}
